// Call manager — context provider mounted once at the staff shell
// (AppLayout). Owns the call state machine + the Supabase Realtime
// subscriptions that drive ring/answer/end signaling. Media (LiveKit
// connect) lives in <InCallSurface> which reads roomName from state.
//
// State machine (each transition logs via console with [call] prefix
// for observability during the two-window live test):
//
//   idle
//    │
//    ├── startCall(calleeId)       → outgoing-ringing  (INSERT calls row)
//    │     │
//    │     ├── 30s timeout         → UPDATE status='missed'           → ended('missed')
//    │     ├── cancelCall()        → UPDATE status='canceled'         → ended('canceled')
//    │     ├── peer UPDATE event:  status='active'                     → active (caller side)
//    │     ├── peer UPDATE event:  status='declined'                   → ended('declined')
//    │     └── peer UPDATE event:  status='missed' (race w/ own timer) → ended('missed')
//    │
//    ├── INSERT event (callee)     → incoming-ringing
//    │     │
//    │     ├── acceptCall()        → UPDATE status='active'           → active (callee side)
//    │     ├── declineCall()       → UPDATE status='declined'         → ended('declined')
//    │     └── peer UPDATE event:  status='canceled'                  → ended('canceled')
//    │
//    └── active
//          │
//          ├── endCall()           → UPDATE status='ended'            → ended('ended')
//          └── peer UPDATE event:  status='ended'                     → ended('ended')
//
//   ended  → auto-clears to idle after ~2.5s (or dismissEnded() now)
//
// Realtime subscriptions (RLS-respecting per the calls migration —
// non-participants get no events regardless of filter):
//
//   incoming-calls:<meId>   .on INSERT WHERE callee_id=eq.<meId>
//   call:<currentCallId>    .on UPDATE WHERE id=eq.<currentCallId>
//
// The per-call channel binds at startCall (caller) or on the incoming
// INSERT (callee) and unbinds when the call resolves.
//
// Spike scope: ONE active call slot. A second incoming ring that arrives
// while busy is logged + ignored. The calls table HAS no DB-level
// uniqueness on (callee_id, status='ringing') — multi-call UI is a
// follow-up dispatch.

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

type EndReason = 'declined' | 'missed' | 'canceled' | 'ended' | 'failed'

export type CallState =
  | { kind: 'idle' }
  | { kind: 'outgoing-ringing'; callId: string; calleeId: string; calleeName: string; roomName: string }
  | { kind: 'incoming-ringing'; callId: string; callerId: string; callerName: string; roomName: string }
  | { kind: 'active'; callId: string; roomName: string; peerId: string; peerName: string; iAmCaller: boolean }
  | { kind: 'ended'; reason: EndReason; message: string; durationSec?: number }
  // Group-incoming ring. Resolved from the call_participants INSERT event +
  // a follow-up SELECT on calls (admitted via calls_session_participant_select
  // because the invitee already has their cp row). originConversationId
  // points back at the team_conversations row the call was launched from,
  // for UI affordances like "Group call from #general".
  | { kind: 'group-incoming-ringing'
      callId: string
      roomName: string
      initiatorId: string
      initiatorName: string
      originConversationId: string | null
      participantCount: number
    }
  // Transient state between Accept and being live in the room. Lives until
  // the group-session sub observes the session flip to 'active' (the
  // step-3d trigger fires on the first joiner), at which point we
  // transition to 'group-active' and mount the in-call grid.
  | { kind: 'group-joining'; callId: string; roomName: string }
  // Live in the group room. Drives GroupInCallSurface (lazy). conversationId
  // is the team_conversations the call was launched from (when available)
  // — useful for future "back to chat" affordances. Per-participant data
  // is queried inside the surface from the LiveKit Room + a one-time
  // roster name lookup against call_participants — no need to denormalize
  // the roster into CallState.
  | { kind: 'group-active'; callId: string; roomName: string; conversationId: string | null }

export interface CallManagerCtx {
  state: CallState
  startCall: (calleeId: string, calleeName?: string) => Promise<void>
  acceptCall: () => Promise<void>
  declineCall: () => Promise<void>
  cancelCall: () => Promise<void>
  endCall: () => Promise<void>
  dismissEnded: () => void
  // Group-call write layer. These are thin wrappers over the DB primitives:
  // start_group_call RPC (atomic session+participants), and per-participant
  // UPDATEs to call_participants under the cp_update RLS policy (user_id =
  // auth.uid()). They do NOT drive state transitions yet — wiring into the
  // CallState machine + per-participant Realtime subscriptions is a follow-
  // up piece. Callable now so the UI layer can be built against them in
  // parallel.
  startGroupCall: (conversationId: string) => Promise<{ callId: string; roomName: string } | null>
  joinGroupCall:  (callId: string) => Promise<boolean>
  declineGroupCall: (callId: string) => Promise<boolean>
  leaveGroupCall: (callId: string) => Promise<boolean>
  // State-aware wrappers the ring UI calls. Read current state, dispatch the
  // raw write, and drive the state machine — mirroring acceptCall/declineCall
  // for 1:1.
  acceptGroupCall: () => Promise<void>
  declineGroupInvite: () => Promise<void>
  // Leaver-side end. Writes 'left' to the caller's own cp row, then drives
  // the local state machine to 'ended' so the in-call surface unmounts
  // (disconnecting the LiveKit room via its cleanup effect). The session
  // stays 'active' in the DB — other participants keep their call.
  leaveGroupCallActive: () => Promise<void>
}

// Exported so a component rendered outside the provider can useContext()
// it directly + check null + gracefully no-op (instead of throwing). The
// useCallManager() hook below is the normal entry point and throws if
// the provider isn't mounted.
export const CallManagerContext = createContext<CallManagerCtx | null>(null)

export function useCallManager(): CallManagerCtx {
  const c = useContext(CallManagerContext)
  if (!c) throw new Error('useCallManager must be used within CallManagerProvider')
  return c
}

const CALL_TIMEOUT_MS = 30_000
const ENDED_DISPLAY_MS = 2500
const log = (...args: unknown[]) => { try { console.log('[call]', ...args) } catch { /* ignore */ } }

export function CallManagerProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth()
  const meId = profile?.id ?? null
  const myWorkspaceId = profile?.workspace_id ?? null

  const [state, setState] = useState<CallState>({ kind: 'idle' })

  // Refs so callbacks always see fresh state without re-binding subscriptions.
  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state }, [state])

  const timeoutRef = useRef<number | null>(null)
  const endedClearRef = useRef<number | null>(null)
  const callChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)
  // Per-call group channels (observe-only this piece): roster watches every
  // call_participants UPDATE for the current call (joins/declines/leaves),
  // session watches the calls row (the ringing→active flip from the
  // step-3d trigger, and the eventual end). Mirror callChannelRef's
  // single-channel-at-a-time discipline — cleanupGroupChannels is called
  // before re-attaching for a different call.
  const groupRosterChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)
  const groupSessionChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)
  // Wall-clock stamp of the active-transition (local Date.now()). Set on
  // both sides at the moment we flip to 'active'; cleared on every
  // finishWithReason so a subsequent call from 'ended' starts clean. Used
  // ONLY when reason === 'ended' (= the call was answered); the other
  // reasons never connected, so duration is meaningless for them.
  const activeStartedAtRef = useRef<number | null>(null)

  const clearTimeoutSafe = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const cleanupCallChannel = useCallback(() => {
    const ch = callChannelRef.current
    if (ch) {
      log('unsubscribe per-call channel')
      supabase.removeChannel(ch).catch(() => { /* ignore */ })
      callChannelRef.current = null
    }
  }, [])

  const cleanupGroupChannels = useCallback(() => {
    const r = groupRosterChannelRef.current
    if (r) {
      log('unsubscribe group-roster channel')
      supabase.removeChannel(r).catch(() => { /* ignore */ })
      groupRosterChannelRef.current = null
    }
    const s = groupSessionChannelRef.current
    if (s) {
      log('unsubscribe group-session channel')
      supabase.removeChannel(s).catch(() => { /* ignore */ })
      groupSessionChannelRef.current = null
    }
  }, [])

  const finishWithReason = useCallback((reason: EndReason, message: string) => {
    clearTimeoutSafe()
    cleanupCallChannel()
    cleanupGroupChannels()  // symmetric — no-op when no group channels are attached
    // Read + always-clear the active-start stamp. Duration is only
    // surfaced for 'ended' (the answered-then-hung-up reason); the other
    // four reasons never reached active, so durationSec stays undefined.
    const startedAt = activeStartedAtRef.current
    activeStartedAtRef.current = null
    const durationSec =
      reason === 'ended' && startedAt != null
        ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
        : undefined
    log('finished →', reason, message, durationSec != null ? `${durationSec}s` : '')
    setState({ kind: 'ended', reason, message, durationSec })
    if (endedClearRef.current != null) window.clearTimeout(endedClearRef.current)
    endedClearRef.current = window.setTimeout(() => {
      endedClearRef.current = null
      setState(s => s.kind === 'ended' ? { kind: 'idle' } : s)
    }, ENDED_DISPLAY_MS)
  }, [clearTimeoutSafe, cleanupCallChannel, cleanupGroupChannels])

  // Attach the per-call roster + session subs for a group call. Used by
  // BOTH the invitee path (the group-invite handler, on incoming-ring
  // resolution) AND the initiator path (startGroupCall, after the RPC
  // returns). Factoring this out keeps the two paths from drifting.
  //
  // Declared AFTER finishWithReason because it captures finishWithReason
  // in its session-sub handler closure.
  //
  // The session sub does double-duty:
  //   1. Drives teardown on terminal session status (ended/canceled/missed).
  //   2. Drives group-joining → group-active when the step-3d trigger flips
  //      session status to 'active'. (The initiator already enters
  //      group-active locally on RPC return, so they skip this transition;
  //      this branch is for the invitee, who is in 'group-joining' until
  //      the first join activates the session.)
  const attachGroupCallChannels = useCallback((callId: string, conversationId: string | null) => {
    cleanupGroupChannels()

    const rosterCh = supabase
      .channel(`group-roster:${callId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'call_participants', filter: `call_id=eq.${callId}` },
        (payload2) => {
          const r = payload2.new as { user_id: string; status: string; role: string }
          log('ROSTER update', { callId, userId: r.user_id, status: r.status, role: r.role })
        })
      .subscribe((status) => log('group-roster channel status=', status))
    groupRosterChannelRef.current = rosterCh

    const sessionCh = supabase
      .channel(`group-session:${callId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'calls', filter: `id=eq.${callId}` },
        (payload2) => {
          const s = payload2.new as { status: string; started_at: string | null; ended_at: string | null }
          log('SESSION update', { callId, status: s.status, startedAt: s.started_at, endedAt: s.ended_at })
          const cur = stateRef.current
          // Extended to also include 'group-active' so a remote end
          // (initiator hangs up, last participant leaves) tears down anyone
          // still in the call.
          const isOurGroupCall =
            (cur.kind === 'group-incoming-ringing' && cur.callId === callId) ||
            (cur.kind === 'group-joining'          && cur.callId === callId) ||
            (cur.kind === 'group-active'           && cur.callId === callId)
          if (!isOurGroupCall) return
          if (s.status === 'ended' || s.status === 'canceled' || s.status === 'missed') {
            finishWithReason('ended', 'Group call ended')
            return
          }
          // The big one: invitee transitions joining → active when the
          // step-3d trigger flips the session on their cp row's status
          // change. activeStartedAtRef is stamped here so the ended-toast
          // duration is consistent with the 1:1 path.
          if (s.status === 'active' && cur.kind === 'group-joining' && cur.callId === callId) {
            activeStartedAtRef.current = Date.now()
            setState({
              kind: 'group-active',
              callId: cur.callId,
              roomName: cur.roomName,
              conversationId,
            })
          }
        })
      .subscribe((status) => log('group-session channel status=', status))
    groupSessionChannelRef.current = sessionCh
  }, [cleanupGroupChannels, finishWithReason])

  const dismissEnded = useCallback(() => {
    if (endedClearRef.current != null) {
      window.clearTimeout(endedClearRef.current)
      endedClearRef.current = null
    }
    setState(s => s.kind === 'ended' ? { kind: 'idle' } : s)
  }, [])

  // ─── Per-call UPDATE subscription ────────────────────────────────────────
  const subscribeToCallUpdates = useCallback((callId: string) => {
    cleanupCallChannel()
    log('subscribe per-call updates', callId)
    const ch = supabase
      .channel(`call:${callId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'calls', filter: `id=eq.${callId}` },
        (payload) => {
          const row = payload.new as { id: string; status: string }
          const cur = stateRef.current
          log('UPDATE event status=', row.status, 'curState=', cur.kind)

          if (cur.kind === 'outgoing-ringing' && cur.callId === callId) {
            if (row.status === 'active') {
              log('outgoing → active (callee accepted)')
              clearTimeoutSafe()
              activeStartedAtRef.current = Date.now()
              setState({
                kind: 'active', callId, roomName: cur.roomName,
                peerId: cur.calleeId, peerName: cur.calleeName, iAmCaller: true,
              })
            } else if (row.status === 'declined') {
              finishWithReason('declined', 'Call declined')
            } else if (row.status === 'missed') {
              finishWithReason('missed', 'No answer')
            } else if (row.status === 'canceled') {
              finishWithReason('canceled', 'Call canceled')
            }
          } else if (cur.kind === 'incoming-ringing' && cur.callId === callId) {
            if (row.status === 'canceled') {
              finishWithReason('canceled', 'Caller hung up')
            } else if (row.status === 'missed') {
              finishWithReason('missed', 'Call timed out')
            }
            // Note: status='active' on incoming side fires when we ACCEPT (we
            // already transitioned locally above). Idempotent — the active
            // branch below treats it as a no-op if state.kind === 'active'.
          } else if (cur.kind === 'active' && cur.callId === callId) {
            if (row.status === 'ended') {
              finishWithReason('ended', 'Call ended')
            }
          }
        })
      .subscribe((status) => log('per-call channel status=', status))
    callChannelRef.current = ch
  }, [cleanupCallChannel, clearTimeoutSafe, finishWithReason])

  // ─── Lifelong INCOMING subscription (callee_id = me) ─────────────────────
  useEffect(() => {
    if (!meId) return
    log('subscribe incoming for', meId)
    const ch = supabase
      .channel(`incoming-calls:${meId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'calls', filter: `callee_id=eq.${meId}` },
        async (payload) => {
          const row = payload.new as {
            id: string; caller_id: string; callee_id: string; room_name: string; status: string
          }
          log('INCOMING INSERT', { id: row.id, caller: row.caller_id, status: row.status })
          if (row.status !== 'ringing') return
          if (stateRef.current.kind !== 'idle' && stateRef.current.kind !== 'ended') {
            log('busy — ignoring incoming (spike: no multi-call UI)')
            return
          }

          // Resolve the caller's display name. profiles read is RLS-restricted
          // to same-workspace per the existing profiles policies; we already
          // know caller is in our workspace because RLS allowed this row.
          const { data: callerProfile } = await supabase
            .from('profiles').select('full_name').eq('id', row.caller_id).maybeSingle()

          log('ring → incoming-ringing')
          setState({
            kind: 'incoming-ringing',
            callId: row.id,
            callerId: row.caller_id,
            callerName: callerProfile?.full_name ?? 'Unknown caller',
            roomName: row.room_name,
          })
          subscribeToCallUpdates(row.id)
        })
      .subscribe((status) => log('incoming channel status=', status))

    return () => {
      log('unsubscribe incoming')
      supabase.removeChannel(ch).catch(() => { /* ignore */ })
    }
  }, [meId, subscribeToCallUpdates])

  // ─── Lifelong GROUP-INVITE subscription (call_participants.user_id = me) ─
  // Observe-only for now: when a fresh ringing invitee row appears for me,
  // resolve the invite metadata (session, initiator name, participant
  // count) and LOG it. State-machine wiring + ring UI come in the next
  // piece — this proves the subscription fires + resolves correctly in
  // isolation. Does NOT setState, does NOT subscribe to per-call channels,
  // does NOT touch the 1:1 sub.
  useEffect(() => {
    if (!meId) return
    log('subscribe group-invites for', meId)
    const ch = supabase
      .channel(`incoming-group-invites:${meId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'call_participants', filter: `user_id=eq.${meId}` },
        async (payload) => {
          const row = payload.new as {
            call_id: string; user_id: string; role: string; status: string
          }
          // The initiator's own cp row is also INSERTed (role='initiator',
          // status='joined') in the same RPC that creates an invite for me.
          // Filter to only the actual incoming-ring case.
          if (row.role !== 'invitee' || row.status !== 'ringing') {
            log('group-invite event ignored (role/status mismatch)', { role: row.role, status: row.status })
            return
          }
          // Symmetric busy-guard: refuse to ring if I'm in any other call
          // state. Mirrors the 1:1 incoming sub's guard exactly. The 1:1
          // guard already covers group-incoming-ringing / group-joining
          // (neither is 'idle' nor 'ended'), so adding this here is
          // sufficient to make both sides mutually exclusive without
          // touching the 1:1 path.
          if (stateRef.current.kind !== 'idle' && stateRef.current.kind !== 'ended') {
            log('busy — ignoring group invite')
            return
          }

          // RLS proof: by now I have a cp row for this call, so
          // calls_session_participant_select admits the session row;
          // call_participants_select admits the roster via is_call_participant.
          const [sessionRes, rosterRes] = await Promise.all([
            supabase.from('calls')
              .select('id, room_name, kind, initiator_id, origin_conversation_id')
              .eq('id', row.call_id).maybeSingle(),
            supabase.from('call_participants')
              .select('user_id, status', { count: 'exact', head: false })
              .eq('call_id', row.call_id),
          ])
          const session = sessionRes.data as
            | { id: string; room_name: string; kind: string; initiator_id: string; origin_conversation_id: string | null }
            | null
          if (!session) {
            console.warn('[call] group-invite: session SELECT returned no row (race? RLS?)', { callId: row.call_id, err: sessionRes.error?.message })
            return
          }

          // Initiator name — read after session lookup so we know who to ask about.
          const { data: initProf } = await supabase
            .from('profiles').select('full_name').eq('id', session.initiator_id).maybeSingle()

          log('GROUP INVITE received', {
            callId: session.id,
            roomName: session.room_name,
            kind: session.kind,
            initiatorId: session.initiator_id,
            initiatorName: initProf?.full_name ?? 'Unknown initiator',
            originConversationId: session.origin_conversation_id,
            participantCount: rosterRes.data?.length ?? 0,
          })

          // Attach the per-call subs via the shared helper (initiator path
          // also uses it from inside startGroupCall — keeping both paths
          // calling the same helper avoids drift).
          attachGroupCallChannels(session.id, session.origin_conversation_id)

          // Transition into the ring state. The session sub now drives
          // group-joining → group-active via the helper's session handler;
          // teardown on a terminal session also flows through it.
          setState({
            kind: 'group-incoming-ringing',
            callId: session.id,
            roomName: session.room_name,
            initiatorId: session.initiator_id,
            initiatorName: initProf?.full_name ?? 'Unknown initiator',
            originConversationId: session.origin_conversation_id,
            participantCount: rosterRes.data?.length ?? 0,
          })
        })
      .subscribe((status) => log('group-invites channel status=', status))

    return () => {
      log('unsubscribe group-invites')
      supabase.removeChannel(ch).catch(() => { /* ignore */ })
    }
  }, [meId, attachGroupCallChannels])

  // ─── Actions ─────────────────────────────────────────────────────────────
  const startCall = useCallback(async (calleeId: string, calleeName: string = 'staff') => {
    if (!meId || !myWorkspaceId) { console.error('[call] startCall: not signed in'); return }
    if (stateRef.current.kind !== 'idle' && stateRef.current.kind !== 'ended') {
      log('startCall: already busy, ignoring')
      return
    }
    // Pre-generate the call id so room_name can include it inline (avoids
    // a second UPDATE round-trip just to set the room name).
    const callId = crypto.randomUUID()
    const roomName = `call-${callId}`
    log('startCall →', { callId, calleeId, calleeName, roomName })
    const { error } = await supabase.from('calls').insert({
      id: callId,
      workspace_id: myWorkspaceId,
      caller_id: meId,
      callee_id: calleeId,
      room_name: roomName,
    })
    if (error) {
      console.error('[call] INSERT failed:', error)
      finishWithReason('failed', `Could not start call: ${error.message}`)
      return
    }
    log('outgoing → ringing')
    setState({ kind: 'outgoing-ringing', callId, calleeId, calleeName, roomName })
    subscribeToCallUpdates(callId)

    // 30s timeout: if still ringing, mark missed. The UPDATE event then
    // drives the state transition via the per-call channel.
    clearTimeoutSafe()
    timeoutRef.current = window.setTimeout(async () => {
      const cur = stateRef.current
      if (cur.kind === 'outgoing-ringing' && cur.callId === callId) {
        log('30s timeout → mark missed')
        const { error: e } = await supabase.from('calls')
          .update({ status: 'missed', ended_at: new Date().toISOString() })
          .eq('id', callId).eq('status', 'ringing')
        if (e) console.error('[call] timeout UPDATE failed:', e)
      }
    }, CALL_TIMEOUT_MS)
  }, [meId, myWorkspaceId, finishWithReason, subscribeToCallUpdates, clearTimeoutSafe])

  const acceptCall = useCallback(async () => {
    const cur = stateRef.current
    if (cur.kind !== 'incoming-ringing') return
    log('acceptCall', cur.callId)
    const { error } = await supabase.from('calls')
      .update({ status: 'active', started_at: new Date().toISOString() })
      .eq('id', cur.callId).eq('status', 'ringing')
    if (error) {
      console.error('[call] accept UPDATE failed:', error)
      finishWithReason('failed', `Could not accept: ${error.message}`)
      return
    }
    log('incoming → active')
    activeStartedAtRef.current = Date.now()
    setState({
      kind: 'active', callId: cur.callId, roomName: cur.roomName,
      peerId: cur.callerId, peerName: cur.callerName, iAmCaller: false,
    })
  }, [finishWithReason])

  const declineCall = useCallback(async () => {
    const cur = stateRef.current
    if (cur.kind !== 'incoming-ringing') return
    log('declineCall', cur.callId)
    await supabase.from('calls')
      .update({ status: 'declined', ended_at: new Date().toISOString() })
      .eq('id', cur.callId).eq('status', 'ringing')
    finishWithReason('declined', 'Call declined')
  }, [finishWithReason])

  const cancelCall = useCallback(async () => {
    const cur = stateRef.current
    if (cur.kind !== 'outgoing-ringing') return
    log('cancelCall', cur.callId)
    await supabase.from('calls')
      .update({ status: 'canceled', ended_at: new Date().toISOString() })
      .eq('id', cur.callId).eq('status', 'ringing')
    finishWithReason('canceled', 'Call canceled')
  }, [finishWithReason])

  const endCall = useCallback(async () => {
    const cur = stateRef.current
    if (cur.kind !== 'active') return
    log('endCall', cur.callId)
    await supabase.from('calls')
      .update({ status: 'ended', ended_at: new Date().toISOString() })
      .eq('id', cur.callId).eq('status', 'active')
    finishWithReason('ended', 'Call ended')
  }, [finishWithReason])

  // ─── Group-call write layer ──────────────────────────────────────────────
  // Thin wrappers over the DB primitives. No state transitions here yet —
  // a later piece adds the per-participant Realtime subscriptions that
  // react to these writes and drives the CallState machine accordingly.
  // Safe to call now: each write is idempotent (status guards), RLS-gated
  // (cp_update admits user_id = auth.uid()), and atomic at the row level.

  const startGroupCall = useCallback(async (
    conversationId: string,
  ): Promise<{ callId: string; roomName: string } | null> => {
    // Busy-guard symmetric with startCall (1:1) — don't start a call
    // while in one. Same guard shape as the invite-handler one; the
    // group-active state is correctly classified as "busy".
    if (stateRef.current.kind !== 'idle' && stateRef.current.kind !== 'ended') {
      log('startGroupCall: already busy, ignoring')
      return null
    }
    log('startGroupCall →', { conversationId })
    // RETURNS TABLE(call_id, room_name) → supabase-js returns an array.
    const { data, error } = await supabase
      .rpc('start_group_call', { p_conversation_id: conversationId })
    if (error || !data || data.length === 0) {
      console.error('[call] startGroupCall failed:', error)
      return null
    }
    const row = data[0] as { call_id: string; room_name: string }
    log('startGroupCall ok →', { callId: row.call_id, roomName: row.room_name })

    // Initiator goes straight to group-active — the RPC already wrote
    // their cp row as role='initiator'/status='joined' atomically, so
    // they're live the moment the RPC returns. Stamp activeStartedAtRef
    // for the ended-toast duration, then attach the per-call subs so
    // the initiator observes roster/session updates (this is the
    // initiator-side attachment the previous piece didn't do).
    activeStartedAtRef.current = Date.now()
    setState({
      kind: 'group-active',
      callId: row.call_id,
      roomName: row.room_name,
      conversationId,
    })
    attachGroupCallChannels(row.call_id, conversationId)

    return { callId: row.call_id, roomName: row.room_name }
  }, [attachGroupCallChannels])

  const joinGroupCall = useCallback(async (callId: string): Promise<boolean> => {
    if (!meId) { console.error('[call] joinGroupCall: not signed in'); return false }
    log('joinGroupCall →', callId)
    const { error } = await supabase
      .from('call_participants')
      .update({ status: 'joined', joined_at: new Date().toISOString() })
      .eq('call_id', callId).eq('user_id', meId).eq('status', 'ringing')
    if (error) {
      console.error('[call] joinGroupCall failed:', error)
      return false
    }
    return true
  }, [meId])

  const declineGroupCall = useCallback(async (callId: string): Promise<boolean> => {
    if (!meId) { console.error('[call] declineGroupCall: not signed in'); return false }
    log('declineGroupCall →', callId)
    const { error } = await supabase
      .from('call_participants')
      .update({ status: 'declined' })
      .eq('call_id', callId).eq('user_id', meId).eq('status', 'ringing')
    if (error) {
      console.error('[call] declineGroupCall failed:', error)
      return false
    }
    return true
  }, [meId])

  const leaveGroupCall = useCallback(async (callId: string): Promise<boolean> => {
    if (!meId) { console.error('[call] leaveGroupCall: not signed in'); return false }
    log('leaveGroupCall →', callId)
    const { error } = await supabase
      .from('call_participants')
      .update({ status: 'left', left_at: new Date().toISOString() })
      .eq('call_id', callId).eq('user_id', meId).eq('status', 'joined')
    if (error) {
      console.error('[call] leaveGroupCall failed:', error)
      return false
    }
    return true
  }, [meId])

  // ─── Group ring state-aware actions ──────────────────────────────────────
  // These are the entry points the GroupIncomingCallModal calls. They read
  // current state, dispatch the proven raw writes (joinGroupCall /
  // declineGroupCall), and drive the state transition. Mirrors the
  // acceptCall / declineCall shape for 1:1.

  const acceptGroupCall = useCallback(async () => {
    const cur = stateRef.current
    if (cur.kind !== 'group-incoming-ringing') return
    log('acceptGroupCall', cur.callId)
    const ok = await joinGroupCall(cur.callId)
    if (!ok) {
      finishWithReason('failed', 'Could not join call')
      return
    }
    setState({ kind: 'group-joining', callId: cur.callId, roomName: cur.roomName })
  }, [joinGroupCall, finishWithReason])

  const declineGroupInvite = useCallback(async () => {
    const cur = stateRef.current
    if (cur.kind !== 'group-incoming-ringing') return
    log('declineGroupInvite', cur.callId)
    await declineGroupCall(cur.callId)
    finishWithReason('declined', 'Call declined')
  }, [declineGroupCall, finishWithReason])

  const leaveGroupCallActive = useCallback(async () => {
    const cur = stateRef.current
    if (cur.kind !== 'group-active') return
    log('leaveGroupCallActive', cur.callId)
    // Write 'left' to the caller's cp row. The raw leaveGroupCall's
    // status guard ('joined') admits both initiator and invitee — both
    // have status='joined' once in the call (RPC sets initiator joined
    // immediately; joinGroupCall sets invitee joined on accept).
    await leaveGroupCall(cur.callId)
    // Then drive local end: setState 'ended', clean up channels + the
    // active-start ref, schedule auto-clear to 'idle'. CallOverlay will
    // unmount GroupInCallSurface, whose useEffect cleanup disconnects
    // the LiveKit room (copied from InCallSurface's pattern). The
    // session row stays 'active' in the DB — other participants keep
    // their call.
    finishWithReason('ended', 'You left the call')
  }, [leaveGroupCall, finishWithReason])

  // Unmount cleanup
  useEffect(() => () => {
    clearTimeoutSafe()
    cleanupCallChannel()
    cleanupGroupChannels()
    if (endedClearRef.current != null) window.clearTimeout(endedClearRef.current)
  }, [clearTimeoutSafe, cleanupCallChannel, cleanupGroupChannels])

  return (
    <CallManagerContext.Provider
      value={{
        state, startCall, acceptCall, declineCall, cancelCall, endCall, dismissEnded,
        startGroupCall, joinGroupCall, declineGroupCall, leaveGroupCall,
        acceptGroupCall, declineGroupInvite, leaveGroupCallActive,
      }}
    >
      {children}
    </CallManagerContext.Provider>
  )
}
